import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { type HslaColor, type HslColor } from 'react-colorful';
import { Box, Divider, Form, token } from '@bynder/design-system';
import { useColorPicker } from 'packages/ds/ColorPicker/components/InternalColorPickerContext';
import { useThrottle } from 'packages/hooks/useThrottle';
import { Slider } from './Slider';
import { GradientProvider, useGradient } from './GradientContext';
import { GradientOptions } from './GradientOptions';
import { Saturation } from '../SolidColorPicker/Saturation';
import { HueAndOpacity } from '../SolidColorPicker/HueAndOpacity';
import { ChangeColorType } from '../ChangeColorType';
import { hslColorToSolidColor, solidColorToHslColor } from '../../utils/hsl';
import { type Color, type Gradient, type SolidColor } from '../../types';

type Props = {
    defaultValue: Gradient;
    setValue: (gradient: Color) => void;
    isColorTypeChangeAllowed?: boolean;
};

export function GradientPicker(props: Props) {
    return (
        <GradientProvider defaultValue={props.defaultValue} onChange={props.setValue}>
            <GradientPickerComponent {...props} />
        </GradientProvider>
    );
}

function GradientPickerComponent(props: Props) {
    const { isColorDragging } = useColorPicker();
    const { state, currentColorValue, updateSelectedStop, hueAndOpacityRef, sliderRef, saturationRef, thumbnailRef } =
        useGradient();

    const updateSaturation = useCallback(
        (newColor: HslColor) => {
            hueAndOpacityRef.current?.setSaturationLightness(newColor.s, newColor.l);

            updateSelectedStop({ color: hslColorToSolidColor(newColor) });
            sliderRef.current?.forceUpdate();
            thumbnailRef.current?.forceUpdate();
        },
        [updateSelectedStop],
    );

    const onHueChange = useCallback(
        (newColor: HslaColor | HslColor) => {
            saturationRef.current?.setHueSaturationLightness(newColor);

            updateSelectedStop({ color: hslColorToSolidColor(newColor) });
            sliderRef.current?.forceUpdate();
            thumbnailRef.current?.forceUpdate();
        },
        [updateSelectedStop],
    );

    const onSaturationChangeThrottled = useThrottle(updateSaturation, 80);

    useEffect(() => {
        if (!isColorDragging) {
            onHueChange(currentColorValue);
        }
    }, [currentColorValue]);

    useEffect(() => {
        const color = toColor(state.gradient.stops[state.selected].color);
        saturationRef.current?.setHueSaturationLightness(color);
        hueAndOpacityRef.current?.setValue(color);
    }, [state.selected, state.gradient.type]);

    return (
        <>
            <Saturation ref={saturationRef} defaultValue={currentColorValue} onChange={onSaturationChangeThrottled} />
            {props.isColorTypeChangeAllowed && <ChangeColorType value={props.defaultValue} onChange={props.setValue} />}
            <Container>
                <Form.Row>
                    <Slider ref={sliderRef} />
                </Form.Row>
                <GradientOptions />
            </Container>
            <Divider />
            <Box paddingTop="4">
                <HueAndOpacity
                    ref={hueAndOpacityRef}
                    defaultValue={currentColorValue}
                    onChange={onHueChange}
                    isColorDragging={isColorDragging}
                />
            </Box>
        </>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${token.spacing3};
    padding: 0 ${token.spacing5} ${token.spacing5};
`;

function toColor(value: SolidColor): HslaColor | HslColor {
    const hexColor: string = typeof value === 'string' ? value : value.color;
    const opacity = typeof value === 'string' ? undefined : value.opacity;

    return solidColorToHslColor(hexColor, opacity, undefined);
}
